import { Inbox } from 'lucide-react';
import type { KeyboardEvent } from 'react';

import { useAccountUnreadSum } from '@/features/accounts/useAccountUnreadSum';
import { useUIStore } from '@/store/ui';
import { cn } from '@/lib/utils';

/**
 * Pinned "所有账号" (all accounts) row — always the first row in the sidebar
 * account list, above the per-account rows rendered by `AccountRow`.
 *
 * Selecting it sets `selectedAccountId` to `null`, which the rest of the
 * console now reads as "show the aggregate conversation queue across
 * every account" rather than "nothing selected" (see `store/ui.ts`). `null`
 * is also the store's initial value, so this row renders selected — and the
 * aggregate queue is already showing — before the accounts list has even
 * loaded.
 *
 * Visually mirrors `AccountRow`'s row chrome (padding, hover, selected
 * treatment) but:
 *   - an `Inbox` icon takes the `StatusDot` slot — there's no single
 *     connection status to show for "all accounts";
 *   - no QR re-scan button (QR pairing is inherently per-account);
 *   - the unread badge sums every cached conversations-list query whose
 *     `accountId` param is `null` (the aggregate lists), via the same
 *     cached-list-sum approximation `AccountRow` uses for a single account —
 *     see `useAccountUnreadSum`.
 *
 * Click/keyboard behavior deliberately mirrors `AccountRow.handleSelect`
 * exactly: it only sets `selectedAccountId`. It does NOT clear
 * `selectedConversationId` or navigate — switching accounts today doesn't do
 * that either, so this stays consistent rather than special-casing "all".
 */
export function AllAccountsRow(): React.JSX.Element {
  const selectedAccountId = useUIStore((s) => s.selectedAccountId);
  const setSelectedAccountId = useUIStore((s) => s.setSelectedAccountId);

  const isSelected = selectedAccountId === null;
  const unreadSum = useAccountUnreadSum(null);

  function handleSelect(): void {
    setSelectedAccountId(null);
  }

  function handleRowKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect();
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={handleRowKeyDown}
      aria-pressed={isSelected}
      // Neumorphic: the aggregate entry is the design's always-inset capsule
      // (design §左栏 「所有账号」入口); selection keeps the shared 3px green
      // accent-bar vocabulary + the raised-selected bg tint.
      className={cn(
        'neu-pressable flex w-full cursor-pointer items-center gap-3 rounded-[14px] border-l-[3px] border-transparent px-3.5 py-3 text-left text-sm shadow-neu-inset focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        isSelected && 'border-l-primary bg-[var(--raised-bg)]',
      )}
    >
      <Inbox className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-sm font-bold">所有账号</span>
      {unreadSum > 0 && (
        <span
          aria-label={`${unreadSum} unread`}
          className="neu-gradient flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-extrabold leading-none text-white shadow-neu-flat"
        >
          {unreadSum > 99 ? '99+' : unreadSum}
        </span>
      )}
    </div>
  );
}
